import { FC, ReactNode, useMemo } from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { LeftArrowIcon } from "@icons";
import { useFormikContext } from "formik";
import styles from "./AddClientModal.module.scss";
import {
    AddClientSteps,
    ClientFormSchema,
    StepSchema,
} from "./AddClientSteps/lib";

interface AddClientModalFooterProps {
    loadingAction: "invite" | "skip" | null;
    validators: Record<AddClientSteps, StepSchema>;
    currentStep: AddClientSteps;
    onPrev: () => void;
    onNext: (values: ClientFormSchema) => void;
    onSkip: () => Promise<void>;
    openPlansOverviewModal: () => void;
    isPlanStepBlocked: boolean;
}

export const AddClientModalFooter: FC<AddClientModalFooterProps> = ({
    validators,
    currentStep,
    onPrev,
    onNext,
    onSkip,
    loadingAction,
    openPlansOverviewModal,
    isPlanStepBlocked,
}) => {
    const { values, isSubmitting } = useFormikContext<ClientFormSchema>();

    const isCurrentStepValid = useMemo(
        () => validators[currentStep].safeParse(values[currentStep]).success,
        [currentStep, values, validators],
    );

    let content: ReactNode;

    const goBackButton = (
        <Button
            variant="secondary"
            type="button"
            iconOnly
            onClick={onPrev}
            className="mr-auto"
            size="xl"
            aria-label="Go back"
        >
            <LeftArrowIcon />
        </Button>
    );

    switch (currentStep) {
        case AddClientSteps.CLIENT:
            content = (
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyOptions={{
                        description: "Next",
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    <Button
                        type="button"
                        variant="primary"
                        size="xl"
                        disabled={!isCurrentStepValid}
                        onClick={() => onNext(values)}
                    >
                        Next
                    </Button>
                </WithHotkey>
            );
            break;
        case AddClientSteps.PLAN:
            content = (
                <>
                    {goBackButton}

                    {!values.plan.billingOwnership && (
                        <Button
                            type="button"
                            variant="secondary"
                            size="xl"
                            onClick={openPlansOverviewModal}
                        >
                            Compare all features
                        </Button>
                    )}

                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Next",
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        <Button
                            type="button"
                            variant="primary"
                            size="xl"
                            disabled={!isCurrentStepValid || isPlanStepBlocked}
                            onClick={() => onNext(values)}
                        >
                            Next
                        </Button>
                    </WithHotkey>
                </>
            );
            break;
        case AddClientSteps.INVITE:
            content = (
                <>
                    {goBackButton}
                    <ButtonWithLoader
                        variant="secondary"
                        type="button"
                        loading={loadingAction === "skip"}
                        disabled={loadingAction === "invite" || isSubmitting}
                        size="xl"
                        onClick={onSkip}
                    >
                        Skip
                    </ButtonWithLoader>

                    <ButtonWithLoader
                        variant="primary"
                        type="submit"
                        loading={loadingAction === "invite" || isSubmitting}
                        disabled={
                            !isCurrentStepValid || loadingAction === "skip"
                        }
                        size="xl"
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Invite",
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        Invite
                    </ButtonWithLoader>
                </>
            );
            break;
        case AddClientSteps.BILLING_TRANSFER:
            content = (
                <>
                    {goBackButton}
                    <ButtonWithLoader
                        variant="primary"
                        type="submit"
                        loading={loadingAction === "invite" || isSubmitting}
                        disabled={!isCurrentStepValid}
                        size="xl"
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Invite",
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        Invite
                    </ButtonWithLoader>
                </>
            );
            break;
    }

    return <footer className={styles.footer}>{content}</footer>;
};
